// useGRNFormXL - Sprint 44 Fase 3 Vista 9.
// Hook form GRN: load + save + saveLinea + msg auto-dismiss. Reusa /api/grn.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../../api/apiClient';

const MSG_AUTO_DISMISS_MS = 4000;
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  po_id: null, po_referencia: '',
  proveedor_nombre: '', proveedor_email: '', proveedor_telefono: '',
  referencia: '', fecha_recepcion: today(),
  estado: 'borrador', observaciones: '',
  created_at: null, updated_at: null,
};

export default function useGRNFormXL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const grnId = id ? Number(id) : null;
  const isEdit = Boolean(grnId);

  const [form, setForm]   = useState(EMPTY);
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [tab, setTab]         = useState('basicos');
  const [msg, setMsg]         = useState(null);

  const msgTimer = useRef(null);
  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);
  useEffect(() => () => { if (msgTimer.current) clearTimeout(msgTimer.current); }, []);

  useEffect(() => {
    if (!grnId) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    apiClient.get('/grn/' + grnId).then((r) => {
      if (!alive) return;
      const d = (r && r.data) || {};
      setForm({
        po_id: d.po_id, po_referencia: d.po_referencia || '',
        proveedor_nombre: d.proveedor_nombre || '',
        proveedor_email: d.proveedor_email || '',
        proveedor_telefono: d.proveedor_telefono || '',
        referencia: d.referencia || '',
        fecha_recepcion: (d.fecha_recepcion || today()).slice(0, 10),
        estado: d.estado || 'borrador',
        observaciones: d.observaciones || '',
        created_at: d.created_at || null,
        updated_at: d.updated_at || null,
      });
      setLineas(d.lineas || []);
    }).catch((e) => {
      if (alive) pushMsg('err', (e && e.message) || 'Error al cargar');
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [grnId, pushMsg]);

  const set = useCallback((k, v) => setForm((p) => ({ ...p, [k]: v })), []);

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await apiClient.put('/grn/' + grnId, {
          referencia: form.referencia,
          fecha_recepcion: form.fecha_recepcion,
          observaciones: form.observaciones,
          estado: form.estado,
        });
        pushMsg('ok', 'GRN actualizado');
      } else {
        if (!form.po_id) {
          pushMsg('err', 'Selecciona una PO primero');
          setSaving(false);
          return;
        }
        const r = await apiClient.post('/grn/', {
          po_id: form.po_id,
          fecha_recepcion: form.fecha_recepcion,
          referencia: form.referencia,
          observaciones: form.observaciones,
        });
        const newId = r && r.data && r.data.id;
        pushMsg('ok', 'GRN creado');
        if (newId) navigate('/grn/' + newId + '/edit', { replace: true });
      }
    } catch (e) {
      pushMsg('err', (e && e.data && e.data.message) || (e && e.message) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }, [isEdit, grnId, form, navigate, pushMsg]);

  const onLineaLocal = (lid, field, value) =>
    setLineas((p) => p.map((l) => l.id === lid ? { ...l, [field]: value } : l));

  const onLineaSave = useCallback(async (linea) => {
    try {
      const r = await apiClient.put('/grn/lineas/' + linea.id, {
        cantidad_recibida: Number(linea.cantidad_recibida) || 0,
        cantidad_po: Number(linea.cantidad_po) || 0,
      });
      if (r && r.data) {
        setLineas((p) => p.map((l) => l.id === linea.id ? r.data : l));
      }
    } catch (e) {
      pushMsg('err', (e && e.message) || 'No se pudo guardar la linea');
    }
  }, [pushMsg]);

  return {
    grnId, isEdit, navigate,
    form, set, lineas,
    loading, saving,
    tab, setTab,
    msg, pushMsg, clearMsg: () => setMsg(null),
    onSave, onLineaLocal, onLineaSave,
  };
}
